import { type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';

type FlexProps = {
  // Standard CSS flexbox props
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
  flexDirection?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  flexWrap?: 'nowrap' | 'wrap' | 'wrap-reverse';

  // Legacy grid-styled props for backward compatibility
  align?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';

  // Margin props (grid-styled compatible)
  m?: number | string;
  mt?: number | string;
  mr?: number | string;
  mb?: number | string;
  ml?: number | string;
  mx?: number | string;
  my?: number | string;

  // Padding props (grid-styled compatible)
  p?: number | string;
  pt?: number | string;
  pr?: number | string;
  pb?: number | string;
  pl?: number | string;
  px?: number | string;
  py?: number | string;

  // Additional flex props
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
  gap?: number | string;
  rowGap?: number | string;
  columnGap?: number | string;

  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
} & HTMLAttributes<HTMLDivElement>; // Allow other standard HTML div props

// Helper function to convert alignItems to CSS class names
const getAlignItemsClass = (align: string): string => {
  switch (align) {
    case 'flex-start':
      return 'items-start';
    case 'flex-end':
      return 'items-end';
    case 'center':
      return 'items-center';
    case 'baseline':
      return 'items-baseline';
    case 'stretch':
      return 'items-stretch';
    default:
      return '';
  }
};

// Helper function to convert justifyContent to CSS class names
const getJustifyContentClass = (justify: string): string => {
  switch (justify) {
    case 'flex-start':
      return 'justify-start';
    case 'flex-end':
      return 'justify-end';
    case 'center':
      return 'justify-center';
    case 'space-between':
      return 'justify-between';
    case 'space-around':
      return 'justify-around';
    case 'space-evenly':
      return 'justify-evenly';
    default:
      return '';
  }
};

// Helper function to convert flexDirection to CSS class names
const getFlexDirectionClass = (direction: string): string => {
  switch (direction) {
    case 'row':
      return 'flex-row';
    case 'row-reverse':
      return 'flex-row-reverse';
    case 'column':
      return 'flex-col';
    case 'column-reverse':
      return 'flex-col-reverse';
    default:
      return '';
  }
};

// Helper function to convert flexWrap to CSS class names
const getFlexWrapClass = (wrap: string): string => {
  switch (wrap) {
    case 'nowrap':
      return 'flex-nowrap';
    case 'wrap':
      return 'flex-wrap';
    case 'wrap-reverse':
      return 'flex-wrap-reverse';
    default:
      return '';
  }
};

// Helper function to convert margin values to CSS class names (reused from Box)
const getMarginClass = (type: string, value: number | string): string => {
  if (typeof value === 'string') {
    if (value === 'auto' && (type.includes('ml') || type.includes('mr'))) {
      return `box-${type}-auto`;
    }
    return ''; // Let CSS handle other string values via inline styles
  }

  // Convert numeric values to utility classes
  if (value >= 0 && value <= 4 && Number.isInteger(value)) {
    return `box-${type}-${value}`;
  }

  return ''; // Fallback to inline styles for other values
};

// Helper function to convert spacing values to inline styles (fallback)
const getSpacingValue = (value: number | string): string => {
  if (typeof value === 'string') {
    if (value === 'auto') {
      return 'auto';
    }
    return value;
  }
  return `${value}rem`;
};

export function Flex(props: FlexProps) {
  const {
    alignItems,
    justifyContent,
    flexDirection = 'row',
    flexWrap = 'nowrap',
    align,
    justify,
    m,
    mt,
    mr,
    mb,
    ml,
    mx,
    my,
    children,
    className,
    style,
    ...rest
  } = props;

  // Determine effective values (props override legacy props)
  const effectiveAlign = alignItems || align;
  const effectiveJustify = justifyContent || justify;

  // Generate utility classes
  const utilityClasses = [
    'flex', // Always apply flex display
    getFlexDirectionClass(flexDirection),
    getFlexWrapClass(flexWrap),
    effectiveAlign && getAlignItemsClass(effectiveAlign),
    effectiveJustify && getJustifyContentClass(effectiveJustify),
    m !== undefined && getMarginClass('m', m),
    mt !== undefined && getMarginClass('mt', mt),
    mr !== undefined && getMarginClass('mr', mr),
    mb !== undefined && getMarginClass('mb', mb),
    ml !== undefined && getMarginClass('ml', ml),
    mx !== undefined && getMarginClass('mx', mx),
    my !== undefined && getMarginClass('my', my),
  ]
    .filter(Boolean)
    .join(' ');

  // Build fallback inline styles for values that don't have utility classes
  const fallbackStyle: CSSProperties = {
    // Flexbox fallbacks (for unsupported values)
    ...(effectiveAlign && !getAlignItemsClass(effectiveAlign) && { alignItems: effectiveAlign }),
    ...(effectiveJustify && !getJustifyContentClass(effectiveJustify) && { justifyContent: effectiveJustify }),

    // Margin fallbacks
    ...(m !== undefined && !getMarginClass('m', m) && { margin: getSpacingValue(m) }),
    ...(mt !== undefined && !getMarginClass('mt', mt) && { marginTop: getSpacingValue(mt) }),
    ...(mr !== undefined && !getMarginClass('mr', mr) && { marginRight: getSpacingValue(mr) }),
    ...(mb !== undefined && !getMarginClass('mb', mb) && { marginBottom: getSpacingValue(mb) }),
    ...(ml !== undefined && !getMarginClass('ml', ml) && { marginLeft: getSpacingValue(ml) }),
    ...(mx !== undefined &&
      !getMarginClass('mx', mx) && {
        marginLeft: getSpacingValue(mx),
        marginRight: getSpacingValue(mx),
      }),
    ...(my !== undefined &&
      !getMarginClass('my', my) && {
        marginTop: getSpacingValue(my),
        marginBottom: getSpacingValue(my),
      }),

    // Merge with provided style
    ...style,
  };

  // Remove CSS props from rest to prevent React 19 warnings
  const cssProps = [
    'alignItems',
    'justifyContent',
    'flexDirection',
    'flexWrap',
    'align',
    'justify',
    'm',
    'mt',
    'mr',
    'mb',
    'ml',
    'mx',
    'my',
  ];
  const domProps = Object.fromEntries(Object.entries(rest).filter(([key]) => !cssProps.includes(key)));

  // Combine utility classes with user-provided className
  const finalClassName = [utilityClasses, className].filter(Boolean).join(' ');

  return (
    <div
      className={finalClassName}
      style={Object.keys(fallbackStyle).length > 0 ? fallbackStyle : undefined}
      {...domProps}
    >
      {children}
    </div>
  );
}
